import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export type ProcessingState = 'idle' | 'uploading' | 'processing' | 'succeeded' | 'failed';

export interface ProcessingStatusProps {
  state: ProcessingState;
  etaSeconds?: number;
  errorMessage?: string;
}

const pulseVariants = {
  initial: { opacity: 0.4 },
  animate: {
    opacity: [0.4, 1, 0.4],
    transition: { duration: 2.4, repeat: Infinity, ease: 'easeInOut' }
  }
};

const fadeVariants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
  exit: { opacity: 0, y: -6, transition: { duration: 0.2 } }
};

export function ProcessingStatus({ state, etaSeconds, errorMessage }: ProcessingStatusProps) {
  const { t } = useTranslation();

  if (state === 'idle') {
    return null;
  }

  const statusKey = (() => {
    switch (state) {
      case 'uploading':
        return 'processing.uploading';
      case 'processing':
        return 'processing.processing';
      case 'succeeded':
        return 'processing.succeeded';
      case 'failed':
        return 'processing.failed';
      default:
        return 'processing.processing';
    }
  })();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={state}
        variants={fadeVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="glass-surface grid gap-4 rounded-3xl p-6"
        aria-live={state === 'failed' ? 'assertive' : 'polite'}
      >
        <div className="flex items-center gap-3 text-sm text-ai-text-secondary">
          <motion.span
            className="inline-flex h-3 w-3 rounded-full"
            variants={pulseVariants}
            initial="initial"
            animate="animate"
            style={{
              background:
                state === 'failed'
                  ? 'linear-gradient(135deg, #F87171, #F97316)'
                  : state === 'succeeded'
                  ? 'linear-gradient(135deg, #34D399, #0EA5E9)'
                  : 'linear-gradient(135deg, #6366F1, #0EA5E9)'
            }}
          />
          <span>{t(statusKey)}</span>
        </div>
        {state === 'processing' && etaSeconds ? (
          <p className="text-xs text-ai-text-secondary/80">
            {t('processing.eta', { seconds: etaSeconds })}
          </p>
        ) : null}
        {state === 'failed' && errorMessage ? (
          <p className="text-sm text-ai-danger" role="status">
            {errorMessage}
          </p>
        ) : null}
      </motion.div>
    </AnimatePresence>
  );
}
